import { Store } from '@ngxs/store';
import {
  GetPickupLocationsAction,
  GetRestaurantGeneralAction,
  GetRestaurantProductCategoriesAction,
  GetRestaurantProductsAction
} from '../../../shared/state/restaurant/restaurant.actions';
import { CacheBucket } from '../../interfaces/cache-versions/cache-versions.interface';
import { HappyStoreInfoCacheService } from '../happy/happy-store-info-cache.service';
import { HappyTenantContextService } from '../happy/happy-tenant-context.service';

/**
 * Centralized mapping between server-side cache version buckets and the
 * client-side caches/state slices they invalidate. Each bucket may schedule
 * one or more "actions" - small closures that either dispatch an NGXS
 * refetch action or clear a non-NGXS cache. The reconciler de-duplicates
 * actions by their `id` so that, for example, both `product` and
 * `media.product` changing in the same response result in a single product
 * refetch.
 */
export interface BucketInvalidationAction {
  id: string;
  run: () => void;
}

export interface InvalidatorContext {
  store: Store;
  storeInfoCache: HappyStoreInfoCacheService;
  tenantContext: HappyTenantContextService;
}

export function getBucketInvalidations(
  bucket: CacheBucket,
  ctx: InvalidatorContext
): BucketInvalidationAction[] {
  const refetchProducts: BucketInvalidationAction = {
    id: 'restaurant.products',
    run: () => ctx.store.dispatch(new GetRestaurantProductsAction())
  };
  const refetchCategories: BucketInvalidationAction = {
    id: 'restaurant.categories',
    run: () => ctx.store.dispatch(new GetRestaurantProductCategoriesAction())
  };
  const refetchGeneral: BucketInvalidationAction = {
    id: 'restaurant.general',
    run: () => ctx.store.dispatch(new GetRestaurantGeneralAction())
  };
  const refetchPickup: BucketInvalidationAction = {
    id: 'restaurant.pickup',
    run: () => ctx.store.dispatch(new GetPickupLocationsAction())
  };
  const clearStoreInfo: BucketInvalidationAction = {
    id: 'happy.store-info-cache',
    run: () => ctx.storeInfoCache.remove(ctx.tenantContext.storeApiKey())
  };

  switch (bucket) {
    case 'product':
    case 'inventory':
    case 'media.product':
    case 'addon':
      return [refetchProducts];
    case 'category':
      return [refetchCategories];
    case 'menu':
    case 'media.menu':
      return [refetchProducts, refetchCategories];
    case 'store':
    case 'media.store':
      return [clearStoreInfo, refetchGeneral];
    case 'shipping':
      return [refetchPickup];
    default: {
      const exhaustive: never = bucket;
      void exhaustive;
      return [];
    }
  }
}
